import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { http, vatLabel } from "@/lib/api";
import { toast } from "sonner";
import { PlusCircle, X, Barcode } from "lucide-react";

/**
 * Lightweight "Νέο Είδος" form opened from the POS when the scanner
 * resolves nothing. Captures the *minimum* fields the backend requires
 * to persist a Product — Description, Price, Department (auto-derives
 * VAT), Category — plus the originally-scanned barcode pre-filled into
 * `barcode1` so the next scan finds the freshly registered item.
 *
 * Once saved, the parent receives the new Product object via
 * ``onCreated(prod)`` and is expected to immediately push it onto the
 * cart and return focus to the scanner input.
 */
export default function QuickProductDialog({
  open,
  onOpenChange,
  initialBarcode,
  departments,
  categories,
  onCreated,
}) {
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [deptNum, setDeptNum] = useState("");
  const [catId, setCatId] = useState("");
  const [barcode, setBarcode] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset / hydrate every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setDesc("");
    setPrice("");
    setBarcode(initialBarcode || "");
    setDeptNum("");
    setCatId("");
    setBusy(false);
  }, [open, initialBarcode]);

  const vatRate = useMemo(() => {
    if (!deptNum) return null;
    const d = departments.find((x) => String(x.number) === String(deptNum));
    return d ? Number(d.vat_number) : null;
  }, [deptNum, departments]);

  const handleSave = async () => {
    if (!desc.trim()) {
      toast.error("Συμπληρώστε την περιγραφή");
      return;
    }
    const p = parseFloat(price);
    if (!p || p <= 0) {
      toast.error("Η τιμή πρέπει να είναι μεγαλύτερη του 0");
      return;
    }
    if (!deptNum) {
      toast.error("Επιλέξτε τμήμα ταμειακής");
      return;
    }
    if (!catId) {
      toast.error("Επιλέξτε κατηγορία");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        description: desc.trim(),
        price: p,
        vat_rate: vatRate ?? 3,
        department: Number(deptNum),
        category_id: catId,
        barcode1: barcode.trim() || "",
        barcode2: "",
        barcode3: "",
        is_active: true,
      };
      const r = await http.post("/products", payload);
      toast.success(`«${r.data.description}» καταχωρήθηκε & προστέθηκε στις πωλήσεις`);
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα καταχώρησης");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-xl p-0 gap-0 overflow-hidden"
        data-testid="quick-product-dialog"
      >
        {/* Header */}
        <div className="px-6 py-4 bg-emerald-50 border-b-2 border-emerald-200 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-emerald-100 grid place-items-center shrink-0">
            <PlusCircle size={22} className="text-emerald-700" />
          </div>
          <div>
            <div className="font-heading text-lg font-bold text-emerald-800">
              ΑΜΕΣΗ ΚΑΤΑΧΩΡΗΣΗ ΕΙΔΟΥΣ
            </div>
            <div className="text-[11px] text-slate-600">
              Συμπληρώστε τα ελάχιστα στοιχεία. Το είδος θα προστεθεί αυτόματα στις πωλήσεις.
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 grid gap-3 bg-white max-h-[70vh] overflow-y-auto">
          <div className="grid gap-1.5">
            <Label className="text-sm font-bold">Περιγραφή <span className="text-red-600">*</span></Label>
            <Input
              autoFocus
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="π.χ. Coca Cola 330ml"
              className="h-11 text-base"
              data-testid="quick-prod-desc"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-sm font-bold">Τιμή € <span className="text-red-600">*</span></Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0,00"
                className="h-11 text-base font-mono"
                data-testid="quick-prod-price"
              />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm font-bold flex items-center gap-1.5">
                <Barcode size={12} /> Barcode
              </Label>
              <Input
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="—"
                className="h-11 text-base font-mono"
                data-testid="quick-prod-barcode"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-sm font-bold">Τμήμα Ταμειακής <span className="text-red-600">*</span></Label>
              <Select value={deptNum} onValueChange={setDeptNum}>
                <SelectTrigger className="h-11" data-testid="quick-prod-dept">
                  <SelectValue placeholder="— Επιλέξτε —" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.number} value={String(d.number)}>
                      #{d.number} – {d.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500">
                ΦΠΑ: <span className="font-bold">{vatRate ? vatLabel(vatRate) : "—"}</span>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm font-bold">Κατηγορία <span className="text-red-600">*</span></Label>
              <Select value={catId} onValueChange={setCatId}>
                <SelectTrigger className="h-11" data-testid="quick-prod-category">
                  <SelectValue placeholder="— Επιλέξτε —" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t-2 border-slate-200 bg-slate-50 flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-12 px-6 text-sm font-bold"
            data-testid="quick-prod-cancel"
            disabled={busy}
          >
            <X size={16} className="mr-2" />
            ΑΚΥΡΩΣΗ
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            className="h-12 px-6 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="quick-prod-save"
            disabled={busy}
          >
            {busy ? "Αποθήκευση…" : "ΚΑΤΑΧΩΡΗΣΗ & ΠΩΛΗΣΗ"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
